import { reactKeycloakWebContext } from '@react-keycloak/web/lib/context';
import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import type { KeycloakInstance } from 'keycloak-js';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  SESSION_EXPIRED_EVENT,
  type SessionExpiredDetail,
} from '@/constants/auth-events';
import { HTTP_REQUEST_TIMEOUT_MS } from '@/constants/http';

// Extended AxiosRequestConfig with custom _retry flag to prevent infinite loops
interface AxiosRequestConfigWithRetry extends AxiosRequestConfig {
  _retry?: boolean;
}

// Request queue for managing requests during agent transitions
interface QueuedRequest {
  config: AxiosRequestConfig;
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
  expectedAgent: string;
}

// Enhanced ref type that includes ready state for agent-aware requests
export interface AgentAwareAxiosRef {
  current: AxiosInstance | undefined;
  isReady: boolean;
  currentAgent: string | undefined;
  waitForReady: (
    expectedAgent?: string,
    timeoutMs?: number
  ) => Promise<boolean>;
}

// Custom error class to signal session expiration - prevents React Query retries
class SessionExpiredError extends Error {
  isSessionExpired = true;

  constructor(message: string) {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

/**
 * Dispatches session expired event and returns a non-retryable error
 */
const triggerSessionExpiration = (reason: string): never => {
  window.dispatchEvent(
    new CustomEvent<SessionExpiredDetail>(SESSION_EXPIRED_EVENT, {
      detail: { reason },
    })
  );
  // Throw a specific error that React Query won't retry
  throw new SessionExpiredError(reason);
};

/**
 * Handles 401 Unauthorized errors by attempting to refresh the Keycloak token.
 * If refresh succeeds, only GET requests are replayed with the new token.
 * If refresh fails, triggers session expiration event and throws a non-retryable error.
 *
 * @param error - The axios error object
 * @param keycloak - The Keycloak instance
 * @param initialized - Whether Keycloak is initialized
 * @param axiosInstance - The axios instance to use for retrying the request
 * @returns Promise that resolves with the retried request response, or throws SessionExpiredError
 */
const handle401TokenRefresh = async (
  error: AxiosError,
  keycloak: KeycloakInstance | undefined,
  initialized: boolean,
  axiosInstance: AxiosInstance
): Promise<unknown> => {
  // Check if this is already a retry to prevent infinite loops
  const config = error.config as AxiosRequestConfigWithRetry | undefined;
  const isRetry = config?._retry;

  if (error.response?.status === 401 && keycloak && initialized && !isRetry) {
    try {
      // Mark this request as a retry to prevent infinite loops
      if (config) {
        config._retry = true;
      }

      // Try to refresh the token
      const refreshed = await keycloak.updateToken(30);
      if (refreshed) {
        // Refresh credentials, but never automatically replay a submission.
        const newToken = keycloak.token;
        if (newToken && error.config) {
          if (error.config.method?.toLowerCase() !== 'get') {
            return Promise.reject(error);
          }
          error.config.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance.request(error.config);
        }
      }

      // Token refresh returned false - session expired
      triggerSessionExpiration(
        'Your session has expired. Please log in again to continue.'
      );
    } catch (refreshError) {
      // Check if this is already a SessionExpiredError (from triggerSessionExpiration)
      if (refreshError instanceof SessionExpiredError) {
        throw refreshError;
      }
      // Token refresh failed - session expired
      triggerSessionExpiration(
        'Your session has expired. Please log in again to continue.'
      );
    }
  }

  // For 401 errors that have already been retried, also trigger session expiration
  if (error.response?.status === 401 && isRetry) {
    triggerSessionExpiration(
      'Your session has expired. Please log in again to continue.'
    );
  }

  return Promise.reject(error);
};

/**
 * Check if an error is a session expired error (non-retryable)
 */
export const isSessionExpiredError = (error: unknown): boolean => {
  return error instanceof SessionExpiredError;
};

const useOptionalKeycloak = () => {
  const keycloakContext = useContext(reactKeycloakWebContext);
  return {
    keycloak: keycloakContext?.authClient,
    initialized: keycloakContext?.initialized ?? false,
  };
};

export const usePrivateRequest = (baseURL: string) => {
  const axiosInstance = useRef<AxiosInstance>();
  const { keycloak, initialized } = useOptionalKeycloak();

  const kcToken = keycloak?.token;

  useEffect(() => {
    axiosInstance.current = axios.create({
      baseURL,
      headers:
        initialized && kcToken
          ? { Authorization: `Bearer ${kcToken}` }
          : undefined,
      timeout: HTTP_REQUEST_TIMEOUT_MS,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });

    axiosInstance.current.defaults.headers.common['Save-Data'] = 'on';
    axiosInstance.current.interceptors.response.use(
      response => response,
      async error => {
        // Handle 401 Unauthorized - token expired
        if (axiosInstance.current) {
          return handle401TokenRefresh(
            error,
            keycloak,
            initialized,
            axiosInstance.current
          );
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstance.current = undefined;
    };
  }, [baseURL, initialized, kcToken, keycloak]);

  return axiosInstance;
};

export const useAgentousPrivateRequest = (
  baseURL: string,
  tenentId?: string,
  agent?: string
): AgentAwareAxiosRef => {
  const axiosInstance = useRef<AxiosInstance>();
  const currentAgentRef = useRef<string | undefined>(agent);
  const [isReady, setIsReady] = useState(false);
  const requestQueue = useRef<QueuedRequest[]>([]);
  const isProcessingQueue = useRef<boolean>(false);
  const readyResolversRef = useRef<
    Map<string, Array<(success: boolean) => void>>
  >(new Map());
  const { keycloak, initialized } = useOptionalKeycloak();

  const kcToken = keycloak?.token;

  // Process queued requests
  const processQueue = useCallback(async () => {
    if (isProcessingQueue.current || requestQueue.current.length === 0) {
      return;
    }

    isProcessingQueue.current = true;
    const queue = [...requestQueue.current];
    requestQueue.current = [];

    for (const queuedRequest of queue) {
      try {
        // Validate that the expected agent matches current agent
        if (
          queuedRequest.expectedAgent === currentAgentRef.current &&
          axiosInstance.current
        ) {
          const response = await axiosInstance.current.request(
            queuedRequest.config
          );
          queuedRequest.resolve(response);
        } else {
          // Agent has changed, reject the request
          queuedRequest.reject(new Error('Agent changed during request queue'));
        }
      } catch (error) {
        queuedRequest.reject(error);
      }
    }

    isProcessingQueue.current = false;
  }, []);

  // Wait for the axios instance to be ready for a specific agent
  const waitForReady = useCallback(
    (expectedAgent?: string, timeoutMs: number = 5000): Promise<boolean> => {
      const targetAgent = expectedAgent || agent;

      // If already ready for the expected agent, resolve immediately
      if (
        axiosInstance.current &&
        currentAgentRef.current === targetAgent &&
        isReady
      ) {
        return Promise.resolve(true);
      }

      // If no target agent, resolve immediately (no agent-specific check needed)
      if (!targetAgent) {
        return Promise.resolve(!!axiosInstance.current);
      }

      return new Promise(resolve => {
        // Add resolver to the map
        const resolvers = readyResolversRef.current.get(targetAgent) || [];
        resolvers.push(resolve);
        readyResolversRef.current.set(targetAgent, resolvers);

        // Set timeout
        setTimeout(() => {
          const currentResolvers =
            readyResolversRef.current.get(targetAgent) || [];
          const index = currentResolvers.indexOf(resolve);
          if (index !== -1) {
            currentResolvers.splice(index, 1);
            if (currentResolvers.length === 0) {
              readyResolversRef.current.delete(targetAgent);
            }
            // Check if we're actually ready now (in case state changed during wait)
            if (
              axiosInstance.current &&
              currentAgentRef.current === targetAgent
            ) {
              resolve(true);
            } else {
              resolve(false);
            }
          }
        }, timeoutMs);
      });
    },
    [agent, isReady]
  );

  // Create axios instance synchronously when dependencies change to prevent the "not ready" window during agent switches
  useEffect(() => {
    // Mark as not ready during transition
    setIsReady(false);

    // Build headers conditionally to avoid sending empty headers which cause server errors
    const headers: AxiosRequestConfig['headers'] = {};

    if (initialized && kcToken) {
      headers.Authorization = `Bearer ${kcToken}`;
    }

    if (tenentId) {
      headers['X-Active-Tenant'] = tenentId;
    }

    if (agent) {
      headers['X-Active-Agent'] = agent;
    }

    // Create new axios instance synchronously (no async gaps)
    const newInstance = axios.create({
      baseURL,
      headers,
      timeout: HTTP_REQUEST_TIMEOUT_MS,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });

    newInstance.defaults.headers.common['Save-Data'] = 'on';

    // Add request interceptor to validate agent
    newInstance.interceptors.request.use(
      config => {
        // Validate that the agent header matches expected agent
        const requestAgent = config.headers?.['X-Active-Agent'];
        if (agent && requestAgent !== agent) {
          console.warn(
            `Agent mismatch detected. Expected: ${agent}, Got: ${requestAgent}`
          );
          // Update header to current agent
          if (config.headers) {
            config.headers['X-Active-Agent'] = agent;
          }
        }
        return config;
      },
      error => Promise.reject(error)
    );

    newInstance.interceptors.response.use(
      response => response,
      async error => {
        // Handle 401 Unauthorized - token expired
        return handle401TokenRefresh(error, keycloak, initialized, newInstance);
      }
    );

    // Update refs before setting ready state (atomic update)
    axiosInstance.current = newInstance;
    currentAgentRef.current = agent;

    // Mark as ready
    setIsReady(true);

    // Resolve any waiting promises for this agent
    if (agent) {
      const resolvers = readyResolversRef.current.get(agent) || [];
      resolvers.forEach(resolve => resolve(true));
      readyResolversRef.current.delete(agent);
    }

    // Process any queued requests after instance is ready
    setTimeout(processQueue, 0);

    // IMPORTANT: Do not set axiosInstance.current = undefined in cleanup
    // This prevents the race condition where the instance becomes undefined between cleanup and the new effect running
    return () => {
      // Only cleanup resolvers, not the axios instance
      // The instance will be replaced by the next effect run
    };
  }, [baseURL, initialized, kcToken, tenentId, agent, processQueue, keycloak]);

  // Return an enhanced ref-like object with ready state
  // Use getters to ensure we always read the latest values from refs
  const enhancedRef = useMemo(
    (): AgentAwareAxiosRef => ({
      get current() {
        return axiosInstance.current;
      },
      set current(value: AxiosInstance | undefined) {
        axiosInstance.current = value;
      },
      get isReady() {
        return isReady;
      },
      get currentAgent() {
        return currentAgentRef.current;
      },
      waitForReady,
    }),
    [isReady, waitForReady]
  );

  return enhancedRef;
};

// Enhanced hook for unauthenticated requests with agent validation
export const useUnauthenticatedAgentRequest = (
  baseURL: string,
  agent: string
): AgentAwareAxiosRef => {
  const axiosInstance = useRef<AxiosInstance>();
  const currentAgentRef = useRef<string>(agent);
  const [isReady, setIsReady] = useState(false);
  const readyResolversRef = useRef<
    Map<string, Array<(success: boolean) => void>>
  >(new Map());

  // Wait for the axios instance to be ready for a specific agent
  const waitForReady = useCallback(
    (expectedAgent?: string, timeoutMs: number = 5000): Promise<boolean> => {
      const targetAgent = expectedAgent || agent;

      // If already ready for the expected agent, resolve immediately
      if (
        axiosInstance.current &&
        currentAgentRef.current === targetAgent &&
        isReady
      ) {
        return Promise.resolve(true);
      }

      return new Promise(resolve => {
        // Add resolver to the map
        const resolvers = readyResolversRef.current.get(targetAgent) || [];
        resolvers.push(resolve);
        readyResolversRef.current.set(targetAgent, resolvers);

        // Set timeout
        setTimeout(() => {
          const currentResolvers =
            readyResolversRef.current.get(targetAgent) || [];
          const index = currentResolvers.indexOf(resolve);
          if (index !== -1) {
            currentResolvers.splice(index, 1);
            if (currentResolvers.length === 0) {
              readyResolversRef.current.delete(targetAgent);
            }
            // Check if we're actually ready now
            if (
              axiosInstance.current &&
              currentAgentRef.current === targetAgent
            ) {
              resolve(true);
            } else {
              resolve(false);
            }
          }
        }, timeoutMs);
      });
    },
    [agent, isReady]
  );

  useEffect(() => {
    // Mark as not ready during transition
    setIsReady(false);

    const newInstance = axios.create({
      baseURL,
      headers: {
        'X-Active-Agent': agent,
      },
      timeout: HTTP_REQUEST_TIMEOUT_MS,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });

    newInstance.defaults.headers.common['Save-Data'] = 'on';

    // Add request interceptor to validate agent
    newInstance.interceptors.request.use(
      config => {
        // Ensure agent header is current
        if (config.headers) {
          config.headers['X-Active-Agent'] = agent;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // Update refs before setting ready state
    axiosInstance.current = newInstance;
    currentAgentRef.current = agent;

    // Mark as ready
    setIsReady(true);

    // Resolve any waiting promises for this agent
    const resolvers = readyResolversRef.current.get(agent) || [];
    resolvers.forEach(resolve => resolve(true));
    readyResolversRef.current.delete(agent);

    // IMPORTANT: Do not set axiosInstance.current = undefined in cleanup
    return () => {
      // Only cleanup resolvers, not the axios instance
    };
  }, [baseURL, agent]);

  // Return an enhanced ref-like object with ready state
  // Use getters to ensure we always read the latest values from refs
  const enhancedRef = useMemo(
    (): AgentAwareAxiosRef => ({
      get current() {
        return axiosInstance.current;
      },
      set current(value: AxiosInstance | undefined) {
        axiosInstance.current = value;
      },
      get isReady() {
        return isReady;
      },
      get currentAgent() {
        return currentAgentRef.current;
      },
      waitForReady,
    }),
    [isReady, waitForReady]
  );

  return enhancedRef;
};
